import { useAuth } from '../contexts/AuthContext'

/**
 * Hook to check if the current admin user has a specific granular permission.
 *
 * Usage:
 *   const canDelete = useHasPermission('menu.delete');
 *   const canExport = useHasPermission('orders.export');
 *
 * Returns true for:
 * - Super admin (username 'admin')
 * - Users with no adminPermissions set (legacy/backward compat)
 * - Users whose granular permission is explicitly true
 *
 * Returns false for:
 * - Non-admin users
 * - Users whose granular permission is explicitly false or missing
 */
export function useHasPermission(permissionKey: string): boolean {
  const { user } = useAuth()

  if (!user) return false
  if (user.role !== 'admin') return false

  // If no granular permissions stored, allow all (legacy/super admin)
  const granular = (user as any).granularPermissions
  if (!granular) {
    // Fallback: check adminPermissions for module-level access
    if (!user.adminPermissions) return true // No restrictions = full access

    // Map permission key to module: "menu.delete" → "menu"
    const module = permissionKey.split('.')[0]
    const moduleAllowed =
      user.adminPermissions[module as keyof typeof user.adminPermissions]

    // If module is allowed, allow all actions within it (legacy behavior)
    return moduleAllowed !== false
  }

  // Check specific granular permission
  return granular[permissionKey] === true
}

/**
 * Hook to get all granular permissions for the current admin user.
 * Returns an empty object for non-admin users.
 */
export function useAdminPermissions(): Record<string, boolean> {
  const { user } = useAuth()

  if (!user || user.role !== 'admin') return {}

  const granular = (user as any).granularPermissions
  if (granular) return granular

  // No granular permissions = return empty (useHasPermission handles fallback)
  return {}
}

/**
 * Non-hook version for use outside React components.
 * Pass the user object directly.
 */
export function hasPermission(user: any, permissionKey: string): boolean {
  if (!user) return false
  if (user.role !== 'admin') return false

  const granular = user.granularPermissions
  if (!granular) {
    if (!user.adminPermissions) return true
    const module = permissionKey.split('.')[0]
    return user.adminPermissions[module] !== false
  }

  return granular[permissionKey] === true
}
